<!-- Compact "view options" control for the business view toolbar: a single "View" -->
<!-- button opens one popover covering both entity/application focus and layer -->
<!-- visibility. Available throughout the stekz hierarchy (domain, product, process pages). -->
<!-- In dev mode (IT landscape) only the layer toggle mounts, filtered to the -->
<!-- Improvements layer — which other layers port over is a later decision. -->
<template>
    <template v-if="stek">
        <Button
            ref="triggerRef"
            v-tooltip.top="{
                value: 'Highlight items or hide parts of the view',
                showDelay: 200,
            }"
            class="view-options-trigger"
            severity="secondary"
            size="small"
            icon="pi pi-filter"
            aria-label="Focus"
            @click="togglePopover"
        />
        <Popover ref="popoverRef" id="view_options_popover">
            <div class="view-options-content">
                <template v-if="!dev">
                    <FocusPanel
                        :focusable-entities="focusableEntities"
                        :focused-entity-id="focusedEntityId"
                        :focusable-applications="
                            devModeEnabled ? focusableApplications : []
                        "
                        :focused-application-id="focusedApplicationId"
                        @update:focused-entity-id="handleEntityFocusChange"
                        @update:focused-application-id="
                            handleApplicationFocusChange
                        "
                    />
                    <div
                        v-if="
                            focusableEntities.length > 0 ||
                            focusableApplications.length > 0
                        "
                        class="view-options-divider"
                    />
                </template>
                <LayerTogglePanel
                    :layers="visibleLayers"
                    @toggle="setLayerVisibility"
                />
            </div>
        </Popover>
    </template>
</template>

<script lang="ts" setup>
import Button from "primevue/button";
import Popover from "primevue/popover";
import { computed, onMounted, ref } from "vue";

import FocusPanel from "@/components/BPMN/FocusPanel.vue";
import LayerTogglePanel from "@/components/BPMN/LayerTogglePanel.vue";
import {
    clearApplicationFocus,
    focusedApplicationId,
    setApplicationFocus,
} from "@/composables/bpmn-manager/application-focus";
import {
    clearFocus,
    focusedEntityId,
    setFocus,
} from "@/composables/bpmn-manager/entity-focus";
import {
    registerLayerPanelOpener,
    setLayerVisibility,
    useLayerControls,
} from "@/composables/bpmn-manager/layer";
import { setPathTraceActive } from "@/composables/bpmn-manager/path-trace";
import { useComplianceEntitlement } from "@/composables/compliance-entitlement";
import { useHotKeys } from "@/composables/hotkeys";
import { useStekManager } from "@/composables/stek-manager";
import { useApplicationsManager } from "@/stores/applications-store";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { useUserManager } from "@/stores/user-store";

const props = defineProps<{
    /** Dev-mode (IT landscape) toolbar: layer toggle only, Improvements only. */
    dev?: boolean;
}>();

const { stek } = useStekManager();
const devModeEnabled = computed(() => stek.value?.dev_mode_enabled ?? false);
const { complianceEnabled } = useComplianceEntitlement();
const { externalCommunicationEnabled } = useUserManager();

const { layers } = useLayerControls();
const visibleLayers = computed(() =>
    layers.value.filter((l) => {
        if (props.dev) {
            return l.type === "triage" && externalCommunicationEnabled.value;
        }
        if (l.type === "applications") return devModeEnabled.value;
        if (l.type === "compliancy") return complianceEnabled.value;
        if (l.type === "triage") return externalCommunicationEnabled.value;
        return true;
    })
);

const { entities } = useBusinessEntitiesManager();
const { applications } = useApplicationsManager();

const focusableEntities = computed(() =>
    (entities.value ?? []).map((e) => ({ entity_id: e.identity, name: e.name }))
);

const focusableApplications = computed(() =>
    applications.value.map((a) => ({ identity: a.identity, name: a.name }))
);

const handleEntityFocusChange = (entityId: string | null) => {
    if (entityId) {
        clearApplicationFocus();
        setPathTraceActive(false);
        setFocus(entityId);
    } else {
        clearFocus();
    }
};

const handleApplicationFocusChange = (applicationId: string | null) => {
    if (applicationId) {
        clearFocus();
        setPathTraceActive(false);
        setApplicationFocus(applicationId);
    } else {
        clearApplicationFocus();
    }
};

const popoverRef = ref<InstanceType<typeof Popover>>();
const triggerRef = ref<InstanceType<typeof Button>>();

// Pass the trigger element explicitly so PrimeVue's isTargetClicked check works
// correctly even when event.currentTarget is null (a known PrimeVue/Vue 4.x quirk
// when onClick propagates through ptmi/inheritAttrs:false).
const togglePopover = () => {
    popoverRef.value?.toggle({
        currentTarget: triggerRef.value?.$el,
    } as Event);
};

// Lets Page.vue's "Hidden: X layers" indicator open this panel without a
// direct component reference — see registerLayerPanelOpener()'s doc comment.
onMounted(() => {
    registerLayerPanelOpener(() => {
        setTimeout(() => {
            popoverRef.value?.show(
                { currentTarget: triggerRef.value?.$el } as Event,
                triggerRef.value?.$el
            );
        }, 0);
    });
});

const { addHotKey } = useHotKeys();
addHotKey(
    "f",
    (event) => {
        event.preventDefault();
        togglePopover();
    },
    "Toggle focus panel",
    "BPMN"
);
addHotKey(
    "l",
    (event) => {
        event.preventDefault();
        togglePopover();
    },
    "Toggle layers panel",
    "BPMN"
);
</script>

<style scoped lang="scss">
.view-options-trigger {
    text-decoration: none;
}
</style>

<!-- Unscoped: Popover teleports to body, so the scoped attribute can't reach it.
     Targeted by id to avoid affecting other Popover instances. -->
<style lang="scss">
#view_options_popover {
    margin-block-start: 0;

    &.p-popover::before,
    &.p-popover::after {
        display: none;
    }
}

.view-options-content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
}

.view-options-divider {
    height: 1px;
    background: var(--p-content-border-color);
    margin: 6px 0;
}
</style>
